import React, { useState, useEffect } from "react";
import { Box, Button, Paper, Typography, Chip, Divider, Snackbar, Alert } from "@mui/material";
import { preadviceTasksService } from "../../services/PreadviceTasksService";
import { publicActionService } from "../../services/PublicActionService";
import { PublicAction, PublicLinkActionType, CreatePublicAction } from "../../interfaces/DTO/PublicAction";
import { PreadviceTask, TaskStatus } from "../../interfaces/DTO/tasks/PreadviceTasks";
import { buildPublicTokenUrl, formatPreAdviceTaskType } from "../../utils/featuresFormatter";
import { formatIsoDateWithTime } from "../../utils/namesFormater";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";

interface PreAdviceRequestComponentProps {
  contactId: number;
  contactName?: string;
  nextActivityDate?: Date | string | null;
  leadSource?: string | null;
  leadSourceText?: string | null;
}

export const PreAdviceRequestComponent: React.FC<PreAdviceRequestComponentProps> = ({
  contactId,
}) => {
  const [requesting, setRequesting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [publicActions, setPublicActions] = useState<PublicAction[]>([]);
  const [loadingActions, setLoadingActions] = useState(true);
  const [preadviceTasks, setPreadviceTasks] = useState<PreadviceTask[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [creatingConsent, setCreatingConsent] = useState(false);

  // טעינת PublicActions ו-PreadviceTasks עבור ה-contact
  useEffect(() => {
    const fetchData = async () => {
      try {
        // טעינת PublicActions
        const actions = await publicActionService.getByContactId(contactId);
        setPublicActions(actions);
      } catch (error) {
        console.error("Error fetching public actions:", error);
      } finally {
        setLoadingActions(false);
      }

      try {
        // טעינת PreadviceTasks
        const tasks = await preadviceTasksService.getTasksByContact(contactId);
        setPreadviceTasks(tasks);
      } catch (error) {
        console.error("Error fetching preadvice tasks:", error);
      } finally {
        setLoadingTasks(false);
      }
    };

    fetchData();
  }, [contactId]);

  // בדיקה אם יש בקשה מסוג צפייה בפוליסות
  const hasViewPoliciesConsent = publicActions.some(
    action => action.action_type === PublicLinkActionType.VIEW_P_CONSENT
  );

  // יצירת בקשת הסכמה חדשה
  const handleCreateConsentLink = async () => {
    setCreatingConsent(true);
    try {
      const dto = CreatePublicAction.defaultEmpty();
      dto.contact_id = contactId;
      dto.action_type = PublicLinkActionType.VIEW_P_CONSENT;
      dto.meta_data = null;

      await publicActionService.createToken(dto);

      // רענון רשימת הפעולות
      const actions = await publicActionService.getByContactId(contactId);
      setPublicActions(actions);

      setSnackbarMessage("קישור להסכמה נוצר בהצלחה!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error creating consent link:", error);
      const errorMessage = error?.response?.data?.detail || "שגיאה ביצירת קישור ההסכמה";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setCreatingConsent(false);
    }
  };

  const handleRequestPreAdvice = async () => {
    setRequesting(true);
    setMessage(null);

    try {
      const result = await preadviceTasksService.requestPreAdviceForContact(contactId);
      if (result.success) {
        setMessage("בקשת טרום ייעוץ נשלחה בהצלחה!");
      } else {
        setMessage(`שגיאה: ${result.message}`);
      }
    } catch (error: any) {
      setMessage(`שגיאה בשליחת הבקשה: ${error.message || "Unknown error"}`);
    } finally {
      // רענון רשימת המשימות בכל מקרה (הצלחה או כישלון)
      try {
        const tasks = await preadviceTasksService.getTasksByContact(contactId);
        setPreadviceTasks(tasks);
      } catch (error) {
        console.error("Error fetching preadvice tasks after request:", error);
      }

      setRequesting(false);
    }
  };

  // פורמט תאריך עם שעה
  const formatDateTime = (date: Date | string | null | undefined) => {
    if (!date) return null;
    try {
      const dateStr = typeof date === 'string' ? date : date.toISOString();
      return formatIsoDateWithTime(dateStr);
    } catch {
      return null;
    }
  };

  // קבלת תיאור סוג הפעולה
  const getActionTypeLabel = (actionType: string) => {
    switch (actionType) {
      case PublicLinkActionType.VIEW_P_CONSENT:
        return "צפייה בפוליסות";
      case PublicLinkActionType.SIGN_CONSENT:
        return "חתימה על הסכמה";
      default:
        return actionType;
    }
  };

 

  // קבלת תיאור סטטוס המשימה
  const getTaskStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.PENDING:
        return "ממתין";
      case TaskStatus.IN_PROGRESS:
        return "בתהליך";
      case TaskStatus.REQUEST_SENT:
        return "בקשה נשלחה";
      case TaskStatus.WAITING_FOR_CLIENT:
        return "ממתין ללקוח";
      case TaskStatus.COMPLETED:
        return "הושלם";
      case TaskStatus.FAILED:
        return "נכשל";
      case TaskStatus.CANCELLED:
        return "בוטל";
      case TaskStatus.IDLE:
        return "סרק";
      default:
        return status;
    }
  };

  // קבלת צבע לסטטוס
  const getTaskStatusColor = (status: TaskStatus): "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
    switch (status) {
      case TaskStatus.COMPLETED:
        return "success";
      case TaskStatus.FAILED:
      case TaskStatus.CANCELLED:
        return "error";
      case TaskStatus.IN_PROGRESS:
      case TaskStatus.REQUEST_SENT:
        return "primary";
      case TaskStatus.WAITING_FOR_CLIENT:
        return "warning";
      case TaskStatus.PENDING:
      case TaskStatus.IDLE:
        return "default";
      default:
        return "default";
    }
  };

  // העתקת לינק ללוח
  const handleCopyLink = (secret: string) => {
    const url = buildPublicTokenUrl(secret);
    navigator.clipboard.writeText(url).then(() => {
      setSnackbarMessage("הלינק הועתק ללוח!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }).catch((err) => {
      console.error("Error copying to clipboard:", err);
      setSnackbarMessage("שגיאה בהעתקת הלינק");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    });
  };

  // שליחת לינק למייל
  const handleSendLinkToEmail = async (actionId: number) => {
    try {
      const result = await publicActionService.sendLinkToEmail(actionId);
      setSnackbarMessage(result.message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error("Error sending link to email:", error);
      const errorMessage = error?.response?.data?.detail || "שגיאה בשליחת המייל";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  // סגירת Snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // בדיקה אם יש משימה בתהליך (סטטוסים שמונעים הזמנה חדשה)
  const activeTask = preadviceTasks.find(task =>
    [TaskStatus.PENDING, TaskStatus.IN_PROGRESS, TaskStatus.REQUEST_SENT, TaskStatus.WAITING_FOR_CLIENT].includes(task.status)
  );
  const hasActiveTask = !!activeTask;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        padding: 4,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 6,
          textAlign: "center",
          maxWidth: 600,
          width: "100%",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h2"
          gutterBottom
          sx={{
            fontWeight: "bold",
            marginBottom: 2,
            color: "primary.main"
          }}
        >
          הזמנת טרום ייעוץ
          {activeTask && (
            <>
              {" - "}
              <Chip
                label={getTaskStatusLabel(activeTask.status)}
                color={getTaskStatusColor(activeTask.status)}
                sx={{ fontSize: "1rem", height: "auto", padding: "4px 8px" }}
              />
            </>
          )}
        </Typography>

        {!hasActiveTask && (
          <Typography variant="body1" sx={{ marginBottom: 4, color: "text.secondary" }}>
            לקוח זה טרם ביצע שליפת מידע ממסלקה.
            <br />
            לחץ על הכפתור להזמנת טרום ייעוץ.
          </Typography>
        )}

        {/* הצגת PreadviceTasks אם קיימות */}
        {!loadingTasks && preadviceTasks.length > 0 && (
          <>
            <Divider sx={{ marginY: 3 }} />
            <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: "bold" }}>
              משימות טרום ייעוץ קיימות
            </Typography>
            <Box sx={{ marginBottom: 3 }}>
              {preadviceTasks.map((task) => (
                <Box
                  key={task.id}
                  sx={{
                    padding: 2,
                    marginBottom: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    backgroundColor: "#f9f9f9",
                    textAlign: "right"
                  }}
                >
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>סוג משימה:</strong> {formatPreAdviceTaskType(task.task_type)}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>סטטוס:</strong>{" "}
                    <Chip
                      label={getTaskStatusLabel(task.status)}
                      color={getTaskStatusColor(task.status)}
                      size="small"
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>מזהה חיצוני:</strong> {task.external_id}
                  </Typography>
                  {task.created_at && (
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>
                      <strong>נוצר בתאריך:</strong> {formatDateTime(task.created_at)}
                    </Typography>
                  )}
                  {task.updated_at && (
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>
                      <strong>עודכן בתאריך:</strong> {formatDateTime(task.updated_at)}
                    </Typography>
                  )}
                  {task.fail_reason && (
                    <Typography variant="body2" sx={{ marginTop: 1, color: "error.main" }}>
                      <strong>סיבת כישלון:</strong> {task.fail_reason}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            <Divider sx={{ marginY: 3 }} />
          </>
        )}

        {/* הצגת PublicActions או כפתור ליצירה */}
        {!loadingActions && (
          <>
            <Divider sx={{ marginY: 3 }} />
            <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: "bold" }}>
              בקשת פעולות מלקוחות
            </Typography>

            {/* אם אין בקשה מסוג צפייה בפוליסות - הצג כפתור ליצירה */}
            {!hasViewPoliciesConsent && (
              <Box sx={{ marginBottom: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ marginBottom: 2, color: "text.secondary" }}>
                  לא קיימת בקשה לצפייה בפוליסות עבור לקוח זה.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCreateConsentLink}
                  disabled={creatingConsent}
                >
                  {creatingConsent ? "יוצר קישור..." : "צור קישור להסכמה"}
                </Button>
              </Box>
            )}
            <Box sx={{ marginBottom: 3 }}>
              {publicActions.map((action) => (
                <Box
                  key={action.id}
                  sx={{
                    padding: 2,
                    marginBottom: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    backgroundColor: "#f9f9f9",
                    textAlign: "right"
                  }}
                >
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>סוג:</strong> {getActionTypeLabel(action.action_type)}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    <strong>נוצר בתאריך:</strong> {formatDateTime(action.created_at)}
                  </Typography>
                  {action.filled_at && (
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>
                      <strong>נחתם בתאריך:</strong>{" "}
                      <Chip
                        label={formatDateTime(action.filled_at)}
                        color="success"
                        size="small"
                      />
                    </Typography>
                  )}
                  {!action.filled_at && (
                    <Chip label="לא נחתם" color="warning" size="small" />
                  )}
                  <Typography variant="body2" sx={{ marginTop: 1 }}>
                    <strong>תוקף:</strong> {formatDateTime(action.expires_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 2 }}>
                    <strong>האם נוצל:</strong> {action.is_used ? "כן" : "לא"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyLink(action.secret)}
                    >
                      העתק לינק
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EmailIcon />}
                      onClick={() => handleSendLinkToEmail(action.id)}
                      color="primary"
                    >
                      שלח למייל
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
            <Divider sx={{ marginY: 3 }} />
          </>
        )}

        {!hasActiveTask && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleRequestPreAdvice}
            disabled={requesting}
            sx={{
              fontSize: "1.2rem",
              padding: "12px 40px",
              marginTop: 2,
            }}
          >
            {requesting ? "שולח בקשה..." : "הזמן טרום ייעוץ"}
          </Button>
        )}

        {message && (
          <Typography
            variant="body2"
            sx={{
              marginTop: 3,
              color: message.includes("שגיאה") ? "error.main" : "success.main",
              fontWeight: "bold",
            }}
          >
            {message}
          </Typography>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
